import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  User,
  Vault,
  X,
} from 'lucide-react';
import {
  createVaultCompte,
  deleteVaultCompte,
  fetchVault,
  fetchVaultTotp,
  updateVaultCompte,
  type CompteVault,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

/** Minuscules sans accents, pour que « credit » trouve « Crédit Agricole ». */
function plat(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Le coffre du bureau : les comptes que l'association utilise au quotidien.
 *
 * Il remplace le carnet partagé et les mots de passe envoyés par SMS entre
 * membres du bureau. Chaque carte porte tout ce qu'il faut pour se connecter —
 * identifiant, adresse, mot de passe, code 2FA du moment — et chaque valeur se
 * copie d'un clic : on vient ici en situation de connexion, pas pour lire.
 *
 * Le code 2FA est calculé par le serveur : la clé, qui permettrait de générer
 * les codes pour toujours, ne quitte jamais la base. L'écran ne reçoit que le
 * code du moment, et le redemande à son expiration.
 */
export default function VaultPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  // Sur la grille des droits : la case « Vault », que porte le president au
  // depart, et tout administrateur par son role.
  const aAcces = aLeDroit('voir_vault');

  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [ajout, setAjout] = useState(false);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [recherche, setRecherche] = useState('');

  const vault = useQuery({ queryKey: ['vault'], queryFn: fetchVault, enabled: aAcces });

  // Les codes 2FA seuls, redemandés dès que le plus proche expire : recharger
  // les cartes entières ferait clignoter l'écran toutes les trente secondes.
  const totp = useQuery({
    queryKey: ['vault-totp'],
    queryFn: fetchVaultTotp,
    enabled: aAcces && (vault.data?.comptes ?? []).some((c) => c.aTotp),
    refetchInterval: (query) => {
      const codes = query.state.data?.codes;
      if (!codes || Object.keys(codes).length === 0) return 30000;
      const minimum = Math.min(...Object.values(codes).map((c) => c.expireDans));
      return Math.max(1, minimum) * 1000 + 500;
    },
  });

  const rafraichir = (message: string) => {
    setNotice({ type: 'success', message });
    queryClient.invalidateQueries({ queryKey: ['vault'] });
    queryClient.invalidateQueries({ queryKey: ['vault-totp'] });
  };
  const echec = (e: Error) => setNotice({ type: 'error', message: e.message });

  const supprimer = useMutation({
    mutationFn: deleteVaultCompte,
    onSuccess: (d: any) => rafraichir(d.message),
    onError: echec,
  });

  if (authLoading || (aAcces && vault.isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!aAcces) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">L'accès au coffre dépend de votre fonction au bureau : un administrateur peut l'ouvrir dans la grille de droits.</p>
          </div>
        </div>
      </div>
    );
  }

  // L'option de la plateforme : le serveur répond 403 quand le module n'est
  // pas inclus — l'écran le dit et mène à la souscription.
  if (vault.isError) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Coffre-fort indisponible</p>
            <p className="text-sm mt-1">{(vault.error as Error)?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  const comptes = vault.data?.comptes ?? [];
  const codes = totp.data?.codes ?? {};

  // Filtrage au fil de la saisie, insensible aux accents et à la casse, sur
  // tout ce qui peut servir à retrouver une fiche : service, identifiant,
  // adresse, commentaire.
  const terme = plat(recherche.trim());
  const comptesVisibles =
    terme === ''
      ? comptes
      : comptes.filter((c) =>
          [c.service, c.identifiant, c.email, c.url, c.commentaire]
            .filter(Boolean)
            .some((champ) => plat(String(champ)).includes(terme))
        );

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Vault className="w-7 h-7 text-primary-700" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Coffre-fort</h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              Les comptes du bureau, à portée de clic. Chaque valeur se copie en cliquant dessus.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setAjout(!ajout);
            setEnEdition(null);
            setNotice(null);
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Ajouter un compte
        </button>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <Check className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {ajout && (
        <FormulaireCompte
          onFini={(message) => {
            setAjout(false);
            rafraichir(message);
          }}
          onEchec={echec}
          onAnnuler={() => setAjout(false)}
        />
      )}

      {/* La recherche filtre au fil de la saisie : passé une dizaine de
          fiches, faire défiler la grille est plus lent que taper trois
          lettres. */}
      {comptes.length > 0 && (
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un compte — service, identifiant, site…"
            className="input w-full pl-9"
            aria-label="Rechercher un compte"
          />
        </div>
      )}

      {comptes.length === 0 && !ajout ? (
        <div className="card lg:p-8 text-center space-y-3">
          <KeyRound className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-600 dark:text-gray-400">
            Le coffre est vide. Ajoutez les comptes que le bureau utilise au quotidien —
            banque, hébergeur, réseaux sociaux…
          </p>
        </div>
      ) : comptesVisibles.length === 0 && !ajout ? (
        <p className="card lg:p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Aucun compte ne répond à « {recherche} ».
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          {comptesVisibles.map((compte) =>
            enEdition === compte.id ? (
              <FormulaireCompte
                key={compte.id}
                compte={compte}
                onFini={(message) => {
                  setEnEdition(null);
                  rafraichir(message);
                }}
                onEchec={echec}
                onAnnuler={() => setEnEdition(null)}
              />
            ) : (
              <CarteCompte
                key={compte.id}
                compte={compte}
                totp={codes[String(compte.id)] ?? compte.totp}
                totpFetchedAt={totp.dataUpdatedAt || vault.dataUpdatedAt}
                onEditer={() => {
                  setAjout(false);
                  setEnEdition(compte.id);
                }}
                onSupprimer={() => {
                  if (window.confirm(`Retirer « ${compte.service} » du coffre ?`)) {
                    supprimer.mutate(compte.id);
                  }
                }}
                onCommentaire={(message) => rafraichir(message)}
                onEchec={echec}
              />
            )
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Les mots de passe et les clés 2FA sont chiffrés dans la base. La clé 2FA ne quitte
        jamais le serveur : seul le code du moment est affiché.
      </p>
    </div>
  );
}

/**
 * Une valeur copiable d'un clic, avec l'accusé « Copié ! » sur place.
 */
function ValeurCopiable({
  icone: Icone,
  libelle,
  valeur,
  affichage,
  mono = false,
  lien = false,
}: {
  icone: any;
  libelle: string;
  valeur: string;
  /** Ce que la carte montre, si différent de ce qui part au presse-papier. */
  affichage?: React.ReactNode;
  mono?: boolean;
  lien?: boolean;
}) {
  const [copie, setCopie] = useState(false);

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(valeur);
      setCopie(true);
      setTimeout(() => setCopie(false), 1500);
    } catch {
      // Presse-papier refusé (permission, contexte non sécurisé) : la
      // sélection manuelle reste possible, rien à afficher de plus utile.
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icone className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-xs text-gray-500 dark:text-gray-400 w-20 shrink-0">{libelle}</span>
      <button
        type="button"
        onClick={copier}
        title={`Copier ${libelle.toLowerCase()}`}
        className={`flex-1 min-w-0 text-left text-sm truncate rounded-lg px-2 py-1 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition ${
          mono ? 'font-mono' : ''
        } text-gray-800 dark:text-gray-200`}
      >
        {affichage ?? valeur}
      </button>
      {copie ? (
        <span className="text-xs text-green-600 inline-flex items-center gap-0.5 shrink-0">
          <Check className="w-3.5 h-3.5" /> Copié !
        </span>
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-300 shrink-0" />
      )}
      {lien && (
        <a
          href={valeur}
          target="_blank"
          rel="noopener noreferrer"
          title="Ouvrir le site"
          className="text-gray-400 hover:text-primary-600 shrink-0"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

/**
 * Le logo du site, devant le nom du service.
 *
 * UNE PASTILLE DESSINÉE ICI, ET PAS UNE ICÔNE DEMANDÉE AILLEURS.
 *
 * Cette pastille affichait le favicon servi par Google, à l'adresse
 * www.google.com/s2/favicons?domain=… C'était commode : Google sert l'icône
 * quel que soit l'endroit où le site la range, là où la demander au site
 * lui-même échoue une fois sur deux.
 *
 * Mais le coffre-fort est précisément l'endroit où cela ne se fait pas. Chaque
 * pastille affichée envoyait à Google le nom de domaine d'un compte de
 * l'association : sa banque, sa mairie, sa fédération, son assureur, sa
 * billetterie. Ouvrir l'écran du coffre revenait à réciter à un tiers la liste
 * de tous les endroits où l'association a un compte — avec l'adresse IP du
 * trésorier et l'heure à laquelle il consulte. Rien de tout cela n'est
 * nécessaire pour afficher une vignette de trente-deux pixels.
 *
 * La pastille est donc dessinée sur place, à partir de l'initiale du service.
 * Elle était déjà écrite : c'était le repli quand l'icône ne chargeait pas.
 * Elle devient le cas normal, et le coffre ne parle plus à personne.
 *
 * La couleur est tirée du nom, pas prise au hasard : deux services différents
 * se distinguent d'un coup d'œil dans une longue liste, et le même service
 * garde sa teinte d'une visite à l'autre.
 */
function LogoSite({ url, service }: { url: string | null; service: string }) {
  // Six teintes, assez éloignées pour se distinguer, assez sourdes pour ne pas
  // crier dans une liste. Le choix est déterministe : la somme des lettres.
  const TEINTES = [
    'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
    'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
    'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
  ];

  // L'initiale se prend sur le nom du service, et à défaut sur le domaine :
  // une fiche nommée « » derrière une adresse reste identifiable.
  let initiale = service.trim().charAt(0).toUpperCase();
  if (!initiale && url) {
    try {
      initiale = new URL(url).hostname.replace(/^www\./, '').charAt(0).toUpperCase();
    } catch {
      initiale = '';
    }
  }

  const graine = (service || url || '')
    .split('')
    .reduce((somme, lettre) => somme + lettre.charCodeAt(0), 0);
  const teinte = TEINTES[graine % TEINTES.length];

  return (
    <span
      aria-hidden="true"
      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${teinte}`}
    >
      {initiale || <KeyRound className="w-4 h-4" />}
    </span>
  );
}

function CarteCompte({
  compte,
  totp,
  totpFetchedAt,
  onEditer,
  onSupprimer,
  onCommentaire,
  onEchec,
}: {
  compte: CompteVault;
  totp: { code: string; expireDans: number } | null;
  totpFetchedAt: number;
  onEditer: () => void;
  onSupprimer: () => void;
  onCommentaire: (message: string) => void;
  onEchec: (e: Error) => void;
}) {
  const [voirMotDePasse, setVoirMotDePasse] = useState(false);
  const [commentaire, setCommentaire] = useState(compte.commentaire ?? '');

  const enregistrerCommentaire = useMutation({
    mutationFn: () => updateVaultCompte(compte.id, { commentaire }),
    onSuccess: () => onCommentaire(`Commentaire de « ${compte.service} » enregistré.`),
    onError: onEchec,
  });

  return (
    <article className="card lg:p-5 space-y-3 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <LogoSite url={compte.url} service={compte.service} />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{compte.service}</h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEditer}
            title="Modifier le compte"
            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-gray-700"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onSupprimer}
            title="Retirer du coffre"
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        {compte.identifiant && (
          <ValeurCopiable icone={User} libelle="Identifiant" valeur={compte.identifiant} />
        )}

        {compte.email && (
          <ValeurCopiable icone={Mail} libelle="E-mail" valeur={compte.email} />
        )}

        {compte.url && (
          <ValeurCopiable
            icone={Globe}
            libelle="Site"
            valeur={compte.url}
            affichage={compte.url.replace(/^https?:\/\//, '')}
            lien
          />
        )}

        {compte.motDePasse && (
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <ValeurCopiable
                icone={KeyRound}
                libelle="Mot de passe"
                valeur={compte.motDePasse}
                affichage={voirMotDePasse ? compte.motDePasse : '••••••••••••'}
                mono
              />
            </div>
            <button
              type="button"
              onClick={() => setVoirMotDePasse(!voirMotDePasse)}
              title={voirMotDePasse ? 'Masquer' : 'Afficher'}
              className="text-gray-400 hover:text-gray-600 shrink-0"
            >
              {voirMotDePasse ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        )}

        {compte.aTotp && <CodeTotp totp={totp} fetchedAt={totpFetchedAt} />}
      </div>

      {/* Commentaire libre : la question de sécurité, le nom du titulaire,
          la procédure de déblocage — ce qui n'a pas de champ à lui. */}
      <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1.5 mt-auto">
        <textarea
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder="Commentaire libre — question secrète, titulaire, procédure…"
          rows={2}
          className="input text-sm min-h-[3rem] w-full"
        />
        {commentaire !== (compte.commentaire ?? '') && (
          <button
            type="button"
            onClick={() => enregistrerCommentaire.mutate()}
            disabled={enregistrerCommentaire.isPending}
            className="btn-outline text-xs px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {enregistrerCommentaire.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Save className="w-3 h-3" />
            )}
            Enregistrer le commentaire
          </button>
        )}
      </div>
    </article>
  );
}

/**
 * Couleur du compte à rebours : vert au-dessus de dix secondes, puis la
 * teinte glisse continûment vers le rouge — chaque seconde est un cran de
 * plus, en passant par l'orange, comme le demande l'usage d'un feu.
 */
function couleurDecompte(restant: number): React.CSSProperties {
  // 142 est la teinte du vert du thème, 0 celle du rouge : la progression
  // linéaire entre les deux traverse jaune et orange d'elle-même.
  const teinte = restant >= 10 ? 142 : Math.max(0, (restant / 10) * 142);

  return {
    color: `hsl(${teinte} 75% 30%)`,
    backgroundColor: `hsl(${teinte} 75% 45% / 0.15)`,
  };
}

/**
 * Le code 2FA du moment, avec son compte à rebours.
 *
 * Le décompte se joue localement à partir de l'expiration annoncée par le
 * serveur ; à zéro, la requête des codes se relance d'elle-même.
 */
function CodeTotp({
  totp,
  fetchedAt,
}: {
  totp: { code: string; expireDans: number } | null;
  fetchedAt: number;
}) {
  const [, tic] = useState(0);

  useEffect(() => {
    const minuteur = setInterval(() => tic((t) => t + 1), 1000);
    return () => clearInterval(minuteur);
  }, []);

  if (!totp) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <ShieldCheck className="w-4 h-4 shrink-0" />
        <span className="text-xs">Code 2FA indisponible</span>
      </div>
    );
  }

  const restant = Math.max(0, totp.expireDans - Math.floor((Date.now() - fetchedAt) / 1000));

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <ValeurCopiable
          icone={ShieldCheck}
          libelle="Code 2FA"
          valeur={totp.code}
          affichage={
            <span className="tracking-[0.3em] font-semibold">
              {totp.code.slice(0, 3)} {totp.code.slice(3)}
            </span>
          }
          mono
        />
      </div>
      {/* Vert tant que le code a du temps, puis la teinte glisse du vert au
          rouge sur les dix dernières secondes : l'urgence se voit du coin de
          l'œil, sans avoir à lire le chiffre. */}
      <span
        className="text-xs tabular-nums w-8 text-center rounded-full py-0.5 shrink-0 font-medium"
        style={couleurDecompte(restant)}
      >
        {restant}s
      </span>
    </div>
  );
}

/**
 * Création ou modification d'un compte, dans une carte à la place de la fiche.
 */
function FormulaireCompte({
  compte,
  onFini,
  onEchec,
  onAnnuler,
}: {
  compte?: CompteVault;
  onFini: (message: string) => void;
  onEchec: (e: Error) => void;
  onAnnuler: () => void;
}) {
  const [form, setForm] = useState({
    service: compte?.service ?? '',
    identifiant: compte?.identifiant ?? '',
    email: compte?.email ?? '',
    url: compte?.url ?? '',
    motDePasse: compte?.motDePasse ?? '',
    totpSecret: '',
    commentaire: compte?.commentaire ?? '',
  });

  const enregistrer = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        service: form.service,
        identifiant: form.identifiant,
        email: form.email,
        url: form.url,
        motDePasse: form.motDePasse,
        commentaire: form.commentaire,
      };
      // La clé 2FA enregistrée n'est jamais renvoyée à l'écran : un champ
      // vide en modification signifie « ne pas y toucher », pas « effacer ».
      if (!compte || form.totpSecret.trim() !== '') {
        payload.totpSecret = form.totpSecret;
      }

      return compte ? updateVaultCompte(compte.id, payload) : createVaultCompte(payload);
    },
    onSuccess: (d: any) => onFini(d.message),
    onError: onEchec,
  });

  return (
    <article className="card lg:p-5 space-y-3 border-2 border-primary-200 dark:border-primary-800">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">
          {compte ? `Modifier « ${compte.service} »` : 'Nouveau compte'}
        </h2>
        <button
          type="button"
          onClick={onAnnuler}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
          aria-label="Annuler"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        <input
          className="input"
          placeholder="Nom du service * — ex. Crédit Agricole"
          value={form.service}
          onChange={(e) => setForm({ ...form, service: e.target.value })}
        />
        <input
          className="input"
          placeholder="Identifiant de connexion — n° client ou nom d'utilisateur"
          value={form.identifiant}
          onChange={(e) => setForm({ ...form, identifiant: e.target.value })}
        />
        <input
          className="input"
          type="email"
          placeholder="E-mail du compte — s'il diffère de l'identifiant"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <input
          className="input"
          placeholder="Adresse du site — ex. ca-languedoc.fr"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
        />
        <input
          className="input font-mono"
          type="text"
          autoComplete="off"
          placeholder="Mot de passe"
          value={form.motDePasse}
          onChange={(e) => setForm({ ...form, motDePasse: e.target.value })}
        />
        <div>
          <input
            className="input font-mono"
            autoComplete="off"
            placeholder={compte?.aTotp ? 'Clé 2FA — enregistrée, saisir pour remplacer' : 'Clé 2FA (Base32) — ex. JBSW Y3DP EHPK 3PXP'}
            value={form.totpSecret}
            onChange={(e) => setForm({ ...form, totpSecret: e.target.value })}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            La clé que le service affiche à l'activation de la double authentification —
            celle qu'on scanne d'habitude en QR code.
          </p>
        </div>
        <textarea
          className="input min-h-[3rem]"
          placeholder="Commentaire libre"
          rows={2}
          value={form.commentaire}
          onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
        />
      </div>

      <button
        type="button"
        onClick={() => enregistrer.mutate()}
        disabled={enregistrer.isPending || form.service.trim() === ''}
        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
      >
        {enregistrer.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Save className="w-4 h-4" />
        )}
        {compte ? 'Enregistrer' : 'Ajouter au coffre'}
      </button>
    </article>
  );
}
